import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Email, Lock } from "@mui/icons-material";
import { colors } from "../../theme/theme";
import { useNavigate } from "react-router-dom";
import { CustomFormInput } from "../../Components/Common/CreatePagesComponents";
import { useDispatch, useSelector } from "react-redux";
import { brandLogin, clearBrandError, brandLogout } from "../../redux/slices/brandAuthSlice";
import { isTokenExpired } from "../../redux/slices/adminSlice";

const BrandLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const { token, loading, error } = useSelector((state) => state.brandAuth);

  useEffect(() => {
    dispatch(clearBrandError());
  }, [dispatch]);

  // Redirect if already authenticated as a brand with an unexpired token
  useEffect(() => {
    const storedToken = localStorage.getItem("brandToken");
    if (storedToken && !isTokenExpired(storedToken)) {
      navigate("/dashboard-brands");
    } else if (storedToken) {
      dispatch(brandLogout());
    }
  }, [token, dispatch, navigate]);

  const handleLogin = (e) => {
    e?.preventDefault();
    if (!email || !password) {
      alert("Please enter both email and password");
      return;
    }
    dispatch(brandLogin({ email: email.trim(), password }));
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#F8F9FA",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "440px",
          bgcolor: "#fff",
          borderRadius: "16px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          p: { xs: 3, sm: 5 },
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "16px" }}>
          <img
            src={require("../../assets/images/login-handpik-logo.png")}
            alt="Handpik Logo"
            style={{ width: "160px", height: "auto" }}
          />
        </div>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            fontFamily: "Poppins-Bold",
            fontSize: "22px",
            color: colors.textColor || "#1a1a1a",
            textAlign: "center",
            mb: 0.5,
          }}
        >
          Brand Portal
        </Typography>

        <Typography
          variant="body2"
          sx={{
            fontFamily: "Poppins-Regular",
            color: "#6b7280",
            mb: 3,
            fontSize: "13px",
          }}
        >
          Sign in to view brand performance metrics and manage catalog
        </Typography>

        {/* Error message */}
        {error && (
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: "8px",
              bgcolor: "#FEE2E2",
              color: "#DC2626",
              fontSize: "13px",
              fontFamily: "Poppins-Regular",
              textAlign: "left",
            }}
          >
            {error}
          </Box>
        )}

        <form onSubmit={handleLogin}>
          <CustomFormInput
            value={email}
            setValue={setEmail}
            placeholder="Brand Email*"
            Icon={Email}
          />
          <CustomFormInput
            value={password}
            setValue={setPassword}
            placeholder="Password*"
            type="password"
            Icon={Lock}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              bgcolor: "#1a1a1a",
              color: "white",
              mt: 2,
              mb: 1,
              py: 1.5,
              fontSize: "15px",
              fontFamily: "Poppins-SemiBold",
              borderRadius: "10px",
              textTransform: "none",
              "&:hover": {
                bgcolor: "#333",
              },
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : "Sign In to Brand Portal"}
          </Button>
        </form>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "#9ca3af",
            mt: 3,
            fontSize: "12px",
          }}
        >
          Brand accounts are issued by Handpik administrators.
        </Typography>
      </Box>
    </Box>
  );
};

export default BrandLogin;
